import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { closeToolbarWindow } from 'app/slices/ahuBoardSlice';
import {
  flattenErrors,
  pruneTunnelErrors,
  resetValidation,
} from 'app/slices/ahuValidationSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import {
  validateAll3D,
  validateTunnel,
  validateTunnelOnlyComponents,
  validateOnSectionDestroy,
} from './validationController';

/**
 * Phase 6e1 — the validation manager effect: boot kick + bus receivers + THE
 * error→tint producer (§22.5 — the half the 6c2 tint machine stubbed).
 *
 * Tint producer (error.log.js render:61-88 port): on every errors revision
 * (debounced 1 s like updateErrors), build
 *   accessoryIds[id] = type ('do_not_generate' sticky)
 *   sectionIds[id]   = type (component OR accessory errors, sticky)
 *   sectionErrors[id]= [messages]
 * and emit `accessoriesWithError` + `sectionsWithError` on the bus — the 6c
 * canvas consumes them (updateAccessoriesColor / store + redraw, §3.4 #22/23).
 *
 * Generate gating (`changeGenerateButton`) is store-driven: the slice's
 * allowGenerate feeds CanvasToolbar/BoardContent classNames (§8.1 / parity
 * 154 / the 6a-verifier #li_model_3d / #li_error_log flags).
 */
export function useValidationManager(
  tunnelIds: number[] | null,
  /**
   * The unit the tunnels belong to. Only read for the boot kick, and only to
   * let a HYBRID unit validate in one request instead of one per tunnel —
   * omit it (or pass `isHybrid: false`) and every request this hook makes is
   * the one it made before.
   */
  unit?: { ahuId: number; isHybrid: boolean } | null
): void {
  const dispatch = useAppDispatch();
  const revision = useAppSelector((s) => s.ahuValidation.revision);
  const errors = useAppSelector((s) => s.ahuValidation.errors);
  const count = useAppSelector((s) => s.ahuValidation.count);
  const resolutionInProgress = useAppSelector((s) => s.ahuValidation.resolutionInProgress);
  const openWindow = useAppSelector((s) => s.ahuBoard.toolbar.openWindow);
  const bootKicked = useRef(false);
  const openWindowRef = useRef(openWindow);
  openWindowRef.current = openWindow;
  // Read, never depended on: the kick must still fire on the first tunnel list
  // the board receives, exactly as it does today.
  const unitRef = useRef(unit);
  unitRef.current = unit;

  // Boot kick — legacy: tunnels.section init calls ahu.validate_all_3D().
  useEffect(() => {
    if (bootKicked.current || !tunnelIds || !tunnelIds.length) return;
    bootKicked.current = true;
    void dispatch(validateAll3D(tunnelIds, unitRef.current ?? undefined));
  }, [tunnelIds, dispatch]);

  // Reconcile the error list with the tunnels that still exist (no legacy
  // counterpart). Every delete cascade tells the slice which tunnel it took,
  // and each is only as good as its own bookkeeping — the dual-tunnel case
  // got this wrong, leaving a deleted section's `do_not_generate` error in the
  // Error Manager with Generate disabled behind it. `tunnels` is server truth,
  // re-GET after every delete (SectionToolbarChrome.refetchTunnelIds), so this
  // drops anything whose tunnel went, whichever path removed it.
  useEffect(() => {
    if (!tunnelIds) return;
    dispatch(pruneTunnelErrors(tunnelIds));
  }, [tunnelIds, dispatch]);

  // Bus receivers (§25.3): floor saves / section-destroy cascades / sweeps.
  useEffect(() => {
    const offs = [
      // floor.js `validate:all:tunnels` → every tunnel model debounces 200 ms
      // then ahu.validateTunnel — here one debounce, all (current) tunnels.
      ahuBoardBus.on('validate:all:tunnels', () => {
        window.setTimeout(() => {
          (tunnelIds ?? []).forEach((id) => void dispatch(validateTunnel(id)));
        }, 200);
      }),
      ahuBoardBus.on('validate:tunnel:components:only', (payload) => {
        void dispatch(validateTunnelOnlyComponents(payload));
      }),
      ahuBoardBus.on('validate:tunnel:after:section:destroy', (payload) => {
        void dispatch(
          validateOnSectionDestroy({
            sectionId: payload.sectionId,
            skip: payload.skip,
            tunnelId: payload.tunnelId,
            tunnel_removed: payload.tunnel_removed,
          })
        );
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [dispatch, tunnelIds]);

  // THE tint producer — debounced 1 s (updateErrors timing).
  useEffect(() => {
    if (resolutionInProgress) return undefined;
    const timer = window.setTimeout(() => {
      const flat = flattenErrors(errors);
      const accessoryIds: Record<number, string> = {};
      const sectionIds: Record<number, string> = {};
      const sectionErrors: Record<number, string[]> = {};
      flat.forEach((item) => {
        item.sections.forEach((section) => {
          section.components.forEach((component) => {
            if (component.message) {
              if (sectionIds[section.id] !== 'do_not_generate')
                sectionIds[section.id] = component.type ?? '';
              (sectionErrors[section.id] ??= []).push(component.message);
            }
          });
          section.accessories.forEach((accessory) => {
            if (accessory.message) {
              const accId = Number(accessory.id);
              if (accessoryIds[accId] !== 'do_not_generate')
                accessoryIds[accId] = accessory.type ?? '';
              if (sectionIds[section.id] !== 'do_not_generate')
                sectionIds[section.id] = accessory.type ?? '';
              (sectionErrors[section.id] ??= []).push(accessory.message);
            }
          });
        });
      });
      ahuBoardBus.emit('accessoriesWithError', accessoryIds);
      ahuBoardBus.emit('sectionsWithError', sectionIds, sectionErrors);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [revision, errors, resolutionInProgress]);

  // Window auto-close when the count hits 0 (`$('.li_error_log.active').click()`).
  useEffect(() => {
    if (count === 0 && openWindowRef.current === 'error_log') {
      dispatch(closeToolbarWindow());
    }
  }, [count, dispatch]);

  // Fresh-boot semantics on unmount.
  useEffect(
    () => () => {
      dispatch(resetValidation());
    },
    [dispatch]
  );
}
